import React, { useContext, useState, useMemo } from "react";
import "./EmojiReaction.css";
import { BASE_URL } from "../../helper/constants";
import { emojiDecode } from "../../utils/encryptDecryptFunction";
import { Tab, Tabs } from "react-bootstrap";
import { useSelector } from "react-redux";
import LanguageContext from "../../helper/LanguageContext";
import { EN } from "../../Languages/EN";

/**
 * EmojiReaction - Modal showing who reacted with which emojis
 * Allows users to remove their own reactions
 */
const EmojiReaction = ({
  userList,
  handleRemoveReaction,
  handleClose,
  chatMessage,
}) => {
  const { isGerm, strings } = useContext(LanguageContext);
  const me = useSelector(({ user }) => user);
  const [activeTab, setActiveTab] = useState("all");
  const [isClosing, setIsClosing] = useState(false);

  // Group reactions by emoji
  const emojiGroups = useMemo(() => {
    return Object.values(
      userList.reduce((groups, user) => {
        groups[user.emoji] = groups[user.emoji] || [];
        groups[user.emoji].push(user);
        return groups;
      }, {})
    );
  }, [userList]);

  // Handle removing own reaction
  const handleRemove = (userInfo) => {
    if (userInfo?.chatId && me?.id === userInfo?.sender) {
      const isLastEmoji = userList.length === 1;

      handleRemoveReaction(
        { sender: userInfo.sender, emoji: userInfo.emoji },
        userInfo.chatId
      );

      // Only animate close if removing the last emoji
      if (isLastEmoji) {
        setIsClosing(true);
        setTimeout(handleClose, 150);
      }
    }
  };

  // Close with animation
  const closeModal = () => {
    setIsClosing(true);
    setTimeout(handleClose, 150);
  };

  // Render a single user row
  const UserRow = ({ user }) => {
    const isOwnReaction = me?.id === user?.sender;

    const onRowClick = (e) => {
      if (isOwnReaction) {
        e.stopPropagation();
        handleRemove(user);
      }
    };

    return (
      <div
        className={`record_flex_div ${isOwnReaction ? "removable" : ""}`}
        onClick={onRowClick}
        onMouseDown={isOwnReaction ? (e) => e.preventDefault() : undefined}
        style={isOwnReaction ? { cursor: "pointer", userSelect: "none" } : undefined}
      >
        <div className="record_left_div">
          <div className="img_box">
            <img
              className="emoji-reaction__avatar"
              src={
                user.avatar
                  ? BASE_URL + user.avatar
                  : process.env.PUBLIC_URL + "/assets/img/user-circle.png"
              }
              alt="user-avatar"
            />
          </div>
          <div className="d-flex flex-column">
            <div className="text-black para_text">
              {isOwnReaction ? (isGerm ? "Du" : EN.you) : user.fullName}
            </div>
            {isOwnReaction && (
              <div className="text-gray para_text">{strings.removeMsg}</div>
            )}
          </div>
        </div>
        <div className="record_right_div">{emojiDecode(user.emoji)}</div>
      </div>
    );
  };

  if (userList.length === 0) {
    return null;
  }

  return (
    <div
      className={`message_ready_by tabs_main_div m-0 p-0 ${isClosing ? "closing" : ""}`}
    >
      {/* Close button */}
      <div className="close_icon" onClick={closeModal}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 50 50"
          width="50px"
          height="50px"
        >
          <path d="M 9.15625 6.3125 L 6.3125 9.15625 L 22.15625 25 L 6.21875 40.96875 L 9.03125 43.78125 L 25 27.84375 L 40.9375 43.78125 L 43.78125 40.9375 L 27.84375 25 L 43.6875 9.15625 L 40.84375 6.3125 L 25 22.15625 Z" />
        </svg>
      </div>

      {/* Tabs for filtering by emoji */}
      <Tabs activeKey={activeTab} onSelect={setActiveTab} fill>
        {/* All reactions tab */}
        <Tab eventKey="all" title={`${strings.All} ${userList.length}`}>
          {userList.map((user) => (
            <UserRow key={`${user.sender}-${user.emoji}`} user={user} />
          ))}
        </Tab>

        {/* Individual emoji tabs */}
        {emojiGroups.map((group) => (
          <Tab
            key={group[0]?.emoji}
            eventKey={group[0]?.emoji}
            title={`${emojiDecode(group[0]?.emoji)} ${group.length}`}
          >
            {group.map((user) => (
              <UserRow key={`${user.sender}-${user.emoji}`} user={user} />
            ))}
          </Tab>
        ))}
      </Tabs>
    </div>
  );
};

export default EmojiReaction;
